import type { PageSeo } from '@tvx/api-client';

/**
 * Search and sharing, with the result drawn as a person will see it.
 *
 * The two boxes are empty most of the time, and an empty box does not mean the
 * page has no title — it means the page uses the built-in one. The preview
 * shows what will actually be emitted, which is the only way "leave empty to
 * use the built-in title" is a promise anybody can check.
 */
export function SeoPanel({ url, seo, value, onChange }: {
  url: string;
  seo: PageSeo;
  value: { title: string; description: string; robots: string };
  onChange: (next: { title: string; description: string; robots: string }) => void;
}) {
  const title = value.title || seo.builtin_title;
  const description = value.description || seo.builtin_description;
  const hidden = seo.forced_noindex || value.robots === 'noindex';
  const clip = (v: string, n: number) => (v.length > n ? `${v.slice(0, n - 1).trim()}…` : v);

  return (
    <div className="crd" style={{ padding: '20px 22px' }}>
      <div className="kh" style={{ marginBottom: 12 }}>Search &amp; sharing</div>

      <div className="fld">
        <label className="lbl">Browser / search title</label>
        <input className="inp" value={value.title} placeholder={seo.builtin_title}
          onChange={(e) => onChange({ ...value, title: e.target.value })} />
      </div>
      <div className="fld">
        <label className="lbl">Meta description</label>
        <textarea className="inp ta" rows={3} value={value.description} placeholder={seo.builtin_description}
          onChange={(e) => onChange({ ...value, description: e.target.value })} />
      </div>

      <div className="fld">
        <label className="lbl">How this looks in search</label>
        <div style={{ border: '1px solid var(--line)', borderRadius: 12, padding: '13px 15px',
          background: 'rgba(var(--wash), calc(.02 * var(--washx)))', opacity: hidden ? 0.45 : 1 }}>
          <div style={{ fontSize: 11.5, color: 'var(--tx3)', marginBottom: 3 }}>
            {url.replace(/^https?:\/\//, '').replace(/\/$/, '') || '/'}
          </div>
          <div style={{ fontFamily: 'var(--fd)', fontSize: 15, color: '#8ab4f8', lineHeight: 1.3 }}>
            {clip(title, 60) || '—'}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--tx2)', lineHeight: 1.45, marginTop: 3 }}>
            {clip(description, 155) || '—'}
          </div>
        </div>
        <p className="hint">
          {hidden
            ? 'Hidden from search — this page will not appear at all.'
            : `${title.length}/60 title · ${description.length}/155 description`
              + (title.length > 60 || description.length > 155 ? ' — over the mark, so it will be cut short' : '')}
        </p>
      </div>

      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: seo.forced_noindex ? 'default' : 'pointer' }}>
        <input type="checkbox" style={{ marginTop: 3 }}
          checked={hidden} disabled={seo.forced_noindex}
          title={seo.forced_noindex ? 'This page is never indexed.' : ''}
          onChange={(e) => onChange({ ...value, robots: e.target.checked ? 'noindex' : '' })} />
        <span>
          <span className="lbl" style={{ display: 'block' }}>Hide this page from search engines</span>
          <span className="hint" style={{ margin: '2px 0 0' }}>
            Sends noindex and drops it from the sitemap. The page stays live and anyone with the link can still open it.
          </span>
        </span>
      </label>
    </div>
  );
}
